import * as angular from "angular"
import { convertDatesToJsonUtc, createMap, FieldViewModel, ICrop, ICropYieldScale, IDataLayer, IDatalayerEditModel, IDataLayerType, IOmniaSession, isArrayEmpty, ISeedRateMap, ISeedRateMapCalcParameters, ISeedRateMapCalcPeriod, IVariety, OmniaObjectType, PlanValidationService, SharedFieldsService, SharedLayersService } from "../exports"
import { PlanCropViewModel } from "../planCropViewModel"
import { compareDefaults, getNewPlanDefaultsFromPlan } from "../plans"
import { IModuleState, IPlanCropViewModel, ISeedRatePlan, ISeedRatePlanEditModel, ISeedsService, NewPlanDefaults } from "../types"
import { getSeedQtyForMap, getSeedQtyForPlan } from "../utils"
import { SeedsLoader } from "./loader"

import { getAuthorizedCroppingFilter } from "@omnia/shared/fields"
import { OmniaService } from "@omnia/shared/service"
import { LayerTypeId, OmniaModuleId } from "@omnia/shared/generated/enums"
import { createLookup, getDistinctValues } from "@omnia/shared/utils/array"


const newPlanDefaultsSettingsKey = "seeds.plan.defaults"

export class SeedsService extends OmniaService implements ISeedsService {

    private _state: IModuleState = {}
    private _visibleLayers: any = {}

    private crops: Map<number, ICrop> = new Map()
    private varieties: Map<number, IVariety> = new Map()

    private holdingCrops: ICrop[]
    private holdingVarieties: IVariety[]

    static $inject = [
        "OmniaDatalayersService",
        "OmniaFieldsService",
        "OmniaSession",
        "OmniaPlanValidationService"
    ]

    constructor(
        private datalayerSvc: SharedLayersService,
        private fieldSvc: SharedFieldsService,
        session: IOmniaSession,
        readonly planValidationService: PlanValidationService,
        private api = new SeedsLoader(session.http)) {

        super(session, {
            name: "Seeds",
            module: OmniaModuleId.Seeds,
            dependencies: [fieldSvc, datalayerSvc]
        })

    }

    getRefreshPromise(): ng.IPromise<any> {

        return this.api
            .load(this.session)
            .then(() => {

                this.crops = createMap(this.fieldSvc.crops, c => c.CropId)
                this.varieties = createMap(this.fieldSvc.cropVarieties, v => v.CropVarietyId)

                this.holdingCrops = this.setupHoldingCrops()
                this.holdingVarieties = this.setupHoldingVarieties()

            })


    }

    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Seeds)
                || (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Layers)
                || (s == OmniaModuleId.Stocks)
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }


    getVariety(varietyId: number): IVariety {
        return this.varieties.get(varietyId)
    }

    getHoldingCrops() {
        return this.holdingCrops
    }

    getHoldingVarieties() {
        return this.holdingVarieties
    }

    getCropYieldScales(): ICropYieldScale[] {
        return this.datalayerSvc.getCropYieldScales(this.datalayerSvc.yearId)
    }

    getTopologyService() {
        return this.datalayerSvc.getTopologyService()
    }

    get state(): IModuleState {
        return this._state
    }

    get filter() {
        return this.fieldSvc.filter
    }

    set filter(value) {
        this.fieldSvc.filter = value
    }

    getSupportedCrops(): number[] {
        return this.api.getSupportedCrops()
    }

    getSupportedSowingDates(cropId: number, varietyId: number): ng.IPromise<ISeedRateMapCalcPeriod[]> {

        return this.api.getSupportedSowingDates(cropId).then(r => {

            var variety = r.filter(p => p.VarietyId == varietyId)
            if (variety.length) return variety

            return r.filter(p => !p.VarietyId)

        })

    }

    get fields(): FieldViewModel[] {
        return this.fieldSvc.fields
    }

    get cropping(): FieldViewModel[] {
        return this.fieldSvc.cropping
    }

    get selectedField(): FieldViewModel {

        var s = this.fieldSvc.selectedItems

        if (s) {
            s = s.filter(f => f.isField())
            if (s.length) {
                return s[s.length - 1]
            }
        }

    }

    set selectedField(value: FieldViewModel) {
        var items = (value && (value.isField())) ? [value] : []
        this.fieldSvc.selectItems(items, true)
    }

    get selectedCropping(): FieldViewModel {

        var selection = this.fieldSvc.selectedItems

        if (selection && selection.length) {

            var s = selection[selection.length - 1]
            if (s.isCropping()) return s

        }

        return null

    }

    set selectedCropping(value: FieldViewModel) {
        var items = (value && (value.isCropping())) ? [value] : []
        this.fieldSvc.selectItems(items, true)
    }

    locateItems(items: FieldViewModel[]) {
        this.fieldSvc.locateItems(items)
    }

    loadLayerZones(layers: IDataLayer[]) {
        return this.datalayerSvc.loadLayerZones(layers)
    }

    getLayerTypes() {
        return this.datalayerSvc.getLayerTypes()
    }

    getSeedLayerType(): IDataLayerType {
        return this.getLayerTypes().find(t => t.TypeId == LayerTypeId.SeedRate)
    }

    getFieldLayers(fieldIds: number[]): ng.IPromise<IDataLayer[]> {
        return this.datalayerSvc.getFieldLayers(fieldIds)
    }

    drawSeedRatesMap(planId: number, mapId: number, map: IDatalayerEditModel): ng.IPromise<IDataLayer> {

        map = angular.extend({}, map)

        const url = map.DataLayerId
            ? "/api/seedrates/map/update"
            : "/api/seedrates/map/create"

        const planData = angular.merge({ PlanId: planId, MapId: mapId }, map)

        return this.post(url, planData, { type: OmniaObjectType.SeedPlanMap, action: "u" })

    }

    calculateSeedRatesMap(data: ISeedRateMapCalcParameters, refresh = true): ng.IPromise<IDataLayer> {

        const url = "/api/seedrates/map/calc"

        data = angular.copy(data)

        convertDatesToJsonUtc(data)

        return this.post(url, data, { type: OmniaObjectType.SeedPlanMap, action: "u" }, refresh)

    }

    getPlans() {
        return this.api.getPlans()
    }

    getNewPlanDefaults(): NewPlanDefaults {

        const defaults: NewPlanDefaults = this.session.globalSettings.get(newPlanDefaultsSettingsKey)

        if (defaults) {
            return defaults
        }

        const plans = (this.api.getPlans() ?? []).sort((a, b) => b.SeedRatePlanId - a.SeedRatePlanId)

        const lastPlan = plans[0]

        return getNewPlanDefaultsFromPlan(lastPlan)

    }

    editPlan(model: ISeedRatePlanEditModel): ng.IPromise<ISeedRatePlan> {

        const plan = model.plan

        plan.HoldingId = this.holdingId
        plan.YearId = this.yearId

        const clone = <ISeedRatePlan>angular.extend({}, plan)

        const data = {
            plan: clone,
            ...model
        }

        convertDatesToJsonUtc(data)

        const url = "/api/seedrates/plan/edit"

        const newPlan = !clone.SeedRatePlanId

        const original = newPlan
            ? undefined
            : this.api.getPlans().find(p => p.SeedRatePlanId === clone.SeedRatePlanId)

        const saveDefaults = newPlan || (!compareDefaults(original, plan))

        return this.post(url, data, { type: OmniaObjectType.SeedPlan, action: newPlan ? "c" : "u" })
            .then(plan => {

                if (saveDefaults) {
                    // This is not awaited, as the result can be ignored
                    this.session.globalSettings.set(newPlanDefaultsSettingsKey, getNewPlanDefaultsFromPlan(clone))
                }

                return plan

            })

    }

    deletePlan(planId: number): ng.IPromise<any> {

        const url = "/api/seedrates/plan/delete/" + planId

        return this.delete(url, undefined, { type: OmniaObjectType.SeedPlan, action: "d" })

    }

    deleteMap(datalayerId: number): ng.IPromise<any> {

        const url = "/api/seedrates/map/delete/" + datalayerId

        return this.delete(url, undefined, { type: OmniaObjectType.Layer, action: "d" })

    }

    /**
     * Returns a plan's cropping, or compatible cropping if [includeAllCompatible] is true
     * @param plan Plan
     * @param includeAllCompatible If true, will return all crops with the same variety, regardless of their current association with the plan
     */
    getPlanCropping(plan: ISeedRatePlan, includeAllCompatible: boolean): IPlanCropViewModel[] {

        const { CropId: cropId, VarietyId: varietyId, SeedRatePlanId: planId } = plan

        const otherPlansCropping = this.getPlans()
            .filter(p => (p.SeedRatePlanId !== planId) && (!isArrayEmpty(p.Maps)))
            .flatMap(plan => plan.Maps.map(m => ({ plan, croppingId: m.CroppingId })))

        const otherPlansCroppingLookup = createLookup(otherPlansCropping, p => p.croppingId)

        const map = createMap(plan.Maps, m => m.CroppingId)

        const authorizedFilter= getAuthorizedCroppingFilter(this.fieldSvc)

        const cropping = this.cropping
            .filter(c => c.currentYear && c.hasBoundary() && (cropId === c.cropId))
            .filter(c => {
                if (map.has(c.croppingId)) return true
                if (!includeAllCompatible) return false
                if (!authorizedFilter(c.croppingId)) return false
                if (!varietyId) return true
                if (c.varietyId === varietyId) return true
                return false
            })

        const vm = cropping
            .map(c => {

                const pc = map.get(c.croppingId)
                const otherPlans = otherPlansCroppingLookup[c.croppingId]?.map(p => p.plan)

                const vm = new PlanCropViewModel(c, pc ? plan : undefined, pc, otherPlans)

                return vm

            })
            .sort((a, b) => {
                let r = (a.plan ? 0 : 1) - (b.plan ? 0 : 1)
                r ||= (a.isInOtherPlans ? 1 : 0) - (b.isInOtherPlans ? 1 : 0)
                return r
            })

        return vm

    }

    getLayerVisibility(datalayerId: number) {
        return this._visibleLayers[datalayerId]
    }

    setLayerVisibility(datalayerId: number, visible: boolean) {
        this._visibleLayers[datalayerId] = visible
    }

    getSeedQtyForPlan(plan: ISeedRatePlan): number {
        return getSeedQtyForPlan(plan)
    }

    getSeedsWeightForMap(plan: ISeedRatePlan, map: ISeedRateMap): number {
        return getSeedQtyForMap(plan, map)
    }


    private setupHoldingCrops() {

        const cropping = this.cropping
            .filter(c => c.visible && c.currentYear && Boolean(c.cropId))
            .map(c => c.cropId)

        const plans = this.api.getPlans().map(p => p.CropId)

        const ids = getDistinctValues(cropping.concat(plans), id => id)

        const crops = ids.map(id => this.crops.get(id)).filter(v => !!v)

        return crops


    }


    private setupHoldingVarieties() {

        const cropping = this.cropping
            .filter(c => c.visible && c.currentYear && Boolean(c.varietyId))
            .map(c => c.cropping.VarietyId)

        const plans = this.api.getPlans().map(p => p.VarietyId)

        const ids = getDistinctValues(cropping.concat(plans), id => id)

        const varieties = ids.map(id => this.getVariety(id)).filter(v => !!v)

        return varieties


    }


}

